import { MaxItemsExceededError } from "@excel/errors";
import { BaseXform } from "@excel/xlsx/xform/base-xform";

interface ListXformOptions {
  tag: string;
  always?: boolean;
  count?: boolean;
  empty?: boolean;
  $count?: string;
  $?: any;
  childXform: any;
  maxItems?: number;
}

class ListXform<TChild = any> extends BaseXform<TChild[]> {
  declare protected tag: string;
  declare protected always: boolean;
  declare protected count?: boolean;
  declare protected empty?: boolean;
  declare public $count: string;
  declare public $?: any;
  declare protected childXform: any;
  declare protected maxItems?: number;
  declare public parser: any;

  constructor(options: ListXformOptions) {
    super();

    this.tag = options.tag;
    this.always = !!options.always;
    this.count = options.count;
    this.empty = options.empty;
    this.$count = options.$count ?? "count";
    this.$ = options.$;
    this.childXform = options.childXform;
    this.maxItems = options.maxItems;
  }

  prepare(model: any[], options: any): void {
    const { childXform } = this;
    if (model) {
      model.forEach((childModel, index) => {
        options.index = index;
        childXform.prepare(childModel, options);
      });
    }
  }

  render(xmlStream: any, model?: any[]): void {
    if (this.always || (model && model.length)) {
      xmlStream.openNode(this.tag, this.$);
      if (this.count) {
        xmlStream.addAttribute(this.$count, model?.length ?? 0);
      }

      const { childXform } = this;
      (model ?? []).forEach((childModel, index) => {
        childXform.render(xmlStream, childModel, index);
      });

      xmlStream.closeNode();
    } else if (this.empty) {
      xmlStream.leafNode(this.tag);
    }
  }

  parseOpen(node: any): boolean {
    if (this.parser) {
      this.parser.parseOpen(node);
      return true;
    }
    if (node.name === this.tag) {
      this.model = [];
      return true;
    }
    if (this.childXform.parseOpen(node)) {
      this.parser = this.childXform;
      return true;
    }
    return false;
  }

  parseText(text: string): void {
    if (this.parser) {
      this.parser.parseText(text);
    }
  }

  parseClose(name: string): boolean {
    if (this.parser) {
      if (!this.parser.parseClose(name)) {
        this.model!.push(this.parser.model);
        this.parser = undefined;

        if (this.maxItems && this.model!.length > this.maxItems) {
          throw new MaxItemsExceededError(this.childXform.tag, this.maxItems);
        }
      }
      return true;
    }

    return false;
  }

  reconcile(model: any[], options: any): void {
    if (model) {
      const { childXform } = this;
      model.forEach((childModel: any) => {
        childXform.reconcile(childModel, options);
      });
    }
  }

  reset(): void {
    super.reset();
    if (this.childXform) {
      this.childXform.reset();
    }
  }
}

export { ListXform };
